import cn from 'classnames';
import { format } from 'date-fns';
import * as Dfns from 'date-fns/fp';
import { ReactElement, useMemo, useState } from 'react';
import { MdAdd, MdCancel, MdOutlineTableRows, MdShowChart } from 'react-icons/md';
import { BpmDateString, DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  ForecastKpiViewOption,
  ForecastResultsData,
  getCombinedChannelPlatform
} from '@blisspointmedia/bpm-types/dist/Forecasting';
import { convertDateToUTC } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { Button } from '../../../Components/Button/Button';
import { ChartContainer } from '../../../Components/ChartContainer';
import { DatePicker } from '../../../Components/DatePicker';
import {
  Appearance,
  Design,
  Placement,
  Size,
  StretchMode
} from '../../../Components/designConstants';
import { DownloadDropdown } from '../../../Components/DownloadDropdown';
import { Dropdown } from '../../../Components/Dropdown';
import { DropdownToggleType } from '../../../Components/Dropdown/dropdown-constants';
import { IconButton } from '../../../Components/IconButton';
import { InfoTooltip } from '../../../Components/InfoTooltip';
import { IconSegmentedControlButton } from '../../../Components/SegmentedControlButton';
import { Spinner } from '../../../Components/Spinner';
import { WidgetContainer } from '../../../Components/WidgetContainer';
import { SetError } from '../../../redux/modals';
import { DATE_GROUPING_OPTIONS } from '../../../utils/date-utils';
import { downloadPNG } from '../../../utils/download-utils';
import {
  FlattenedForecastDataResult,
  ForecastChartViewLabels,
  ForecastDataTimestampRanges,
  formatPercentBudget,
  formatRawBudget,
  FullForecastSelection,
  GraphViewOptions
} from '../forecasting-constants';
import {
  dedupeBudgetsWithPrimary,
  excludePrimaryBudget,
  formatForecastViewLabel,
  getForecastDownloadFilename,
  transformAndDownloadDataToCSV
} from '../forecasting-utils';
import { ForecastResultsChart } from './ForecastResultsCharts';
import { ForecastResultsTable } from './ForecastResultsTable';

interface ForecastResultsProps {
  forecast: FullForecastSelection;
  flatForecastData: FlattenedForecastDataResult;
  dataTimestampRanges: ForecastDataTimestampRanges;
  forecastResultsData: ForecastResultsData[];
  budgetSelectableOptions: number[];
  selectNewBudget: (budget: number) => void;
  showWIPFeatures?: boolean;
  chartDateGrouping: DateGroupingKey;
  setChartDateGrouping: (newVal: DateGroupingKey) => void;
  cid: string;
  setError: SetError;
}

export const ForecastResults = ({
  forecast,
  flatForecastData,
  dataTimestampRanges,
  forecastResultsData,
  budgetSelectableOptions,
  selectNewBudget,
  showWIPFeatures = false,
  chartDateGrouping,
  setChartDateGrouping,
  cid,
  setError
}: ForecastResultsProps): ReactElement => {
  const [graphView, setGraphView] = useState<GraphViewOptions>('showGraph');
  const [visibleComparisonBudgets, setVisibleComparisonBudgets] = useState<Set<number>>(new Set());
  const [stagedComparisonBudgets, setStagedComparisonBudgets] = useState<Set<number>>(new Set());
  const [showComparisonBudgetDropdown, setShowComparisonBudgetDropdown] = useState(false);
  const [selectedKpiView, setSelectedKpiView] = useState(ForecastKpiViewOption.TOTAL);
  const [userEarliestDate, setUserEarliestDate] = useState<BpmDateString | null>(null);
  const [userLatestDate, setUserLatestDate] = useState<BpmDateString | null>(null);

  const {
    trainTestData: flatTrainTest,
    historicalData: flatHistorical,
    forecastResultsData: flatForecastResults
  } = flatForecastData;

  const { minimumDate, maximumDate, initialEarliest } = useMemo(() => {
    if (flatTrainTest.length + flatHistorical.length + flatForecastResults.length === 0) {
      return { minimumDate: undefined, maximumDate: undefined, initialEarliest: undefined };
    }

    const lastForecastedDate = flatForecastResults[flatForecastResults.length - 1].date;
    const firstForecastedDate = flatForecastResults[0].date;
    const desiredFirstVisibleDateObj = Dfns.subDays(
      flatForecastResults.length * 2,
      Dfns.parseISO(firstForecastedDate)
    );

    return {
      minimumDate: flatTrainTest[0].date,
      maximumDate: lastForecastedDate,
      initialEarliest: format(
        convertDateToUTC(desiredFirstVisibleDateObj),
        'yyyy-MM-dd'
      ) as BpmDateString
    };
  }, [flatTrainTest, flatHistorical.length, flatForecastResults]);

  const earliestVisibleDate = userEarliestDate ?? initialEarliest;
  const latestVisibleDate = userLatestDate ?? maximumDate;

  const setEarliestVisibleDate = (val: BpmDateString | undefined) =>
    setUserEarliestDate(val ?? null);
  const setLatestVisibleDate = (val: BpmDateString | undefined) => setUserLatestDate(val ?? null);

  const graphDatePickerRange = useMemo(
    () => ({ start: earliestVisibleDate ?? '', end: latestVisibleDate ?? '' }),
    [earliestVisibleDate, latestVisibleDate]
  );

  const tableDatePickerRange = useMemo(
    () => ({ start: minimumDate ?? '', end: maximumDate ?? '' }),
    [maximumDate, minimumDate]
  );

  const orderedSelectedBudgets = useMemo(
    () =>
      excludePrimaryBudget(Array.from(visibleComparisonBudgets), forecast.primaryBudget).sort(
        (a, b) => a - b
      ),
    [forecast.primaryBudget, visibleComparisonBudgets]
  );

  const allVisibleBudgets = useMemo(() => {
    if (visibleComparisonBudgets.size === 0) {
      return [];
    }

    return dedupeBudgetsWithPrimary(orderedSelectedBudgets, forecast.primaryBudget);
  }, [forecast.primaryBudget, orderedSelectedBudgets, visibleComparisonBudgets]);

  const comparisonBudgetMenuItems = useMemo(
    () =>
      excludePrimaryBudget(forecast.comparisonBudgets, forecast.primaryBudget).map(budget => ({
        value: String(budget),
        selected: stagedComparisonBudgets.has(budget),
        label: formatRawBudget(budget),
        trailingContent: (
          <div className='menu-row-label-right'>
            {formatPercentBudget(budget / forecast.primaryBudget)}
          </div>
        )
      })),
    [forecast.comparisonBudgets, forecast.primaryBudget, stagedComparisonBudgets]
  );

  const downloadAsCSV = (includeHistorical = false) => {
    transformAndDownloadDataToCSV({
      forecast,
      data: flatForecastData,
      includeHistorical,
      cid
    });
  };

  const downloadAsPNG = () => {
    const fileName = getForecastDownloadFilename(forecast, cid);
    downloadPNG('.forecastResults', fileName, '', [], false);
  };

  const downloadOptions = [
    {
      label: 'CSV: Forecast',
      onClick: () => downloadAsCSV()
    },
    {
      label: 'CSV: Forecast + Historical',
      onClick: () => downloadAsCSV(true)
    },
    {
      label: 'PNG',
      onClick: downloadAsPNG,
      disabled: graphView === 'showTable'
    }
  ];

  return (
    <WidgetContainer
      collapsible
      header={
        <div className='forecast-widget-header'>
          <span>How will spend on {forecast.channel} affect </span>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            className='forecast-kpi-view-dropdown'
            onChange={val => setSelectedKpiView(val as ForecastKpiViewOption)}
            options={Object.keys(ForecastChartViewLabels[forecast.outcomeVar]).map(key => ({
              value: key,
              label: formatForecastViewLabel(
                forecast.outcomeVar,
                forecast.kpiName,
                key as ForecastKpiViewOption
              ),
              selected: selectedKpiView === (key as ForecastKpiViewOption)
            }))}
            type={DropdownToggleType.WIDGET_TITLE}
            value={formatForecastViewLabel(forecast.outcomeVar, forecast.kpiName, selectedKpiView)}
          />
          <span>?</span>
        </div>
      }
      subHeader={
        <div className='forecast-budget-sub-header'>
          <div className='subheader-text'>
            Forecasted
            <InfoTooltip>
              We use the historical data to understand the temporal relationship of the outcome
              variable relative to factors such as seasonality and daily spend, among other
              variables. This model is then used to forecast future outcomes for specific time
              periods given the historical relationships observed. We use estimates of different
              budgets to understand how the forecasts change depending on budget level.
            </InfoTooltip>
            for a budget of{' '}
          </div>
          {budgetSelectableOptions.length > 0 ? (
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              className='forecast-budget-dropdown'
              size={Size.SM}
              onChange={val => selectNewBudget(Number(val))}
              options={budgetSelectableOptions.map(val => String(val))}
              type={DropdownToggleType.WIDGET_TITLE}
              value={formatRawBudget(forecast.primaryBudget)}
            />
          ) : (
            <Button
              size={Size.LG}
              appearance={Appearance.EMPTY}
              className='forecasting-dropdown-without-options'
            >
              {formatRawBudget(forecast.primaryBudget)}
            </Button>
          )}
          {orderedSelectedBudgets.length > 0 && (
            <div
              className={cn('comparison-budget-tokens-container', {
                disabled: graphView === 'showTable'
              })}
            >
              vs
              {orderedSelectedBudgets.map(budget => (
                <div className='comparison-budget-token' key={`comparison-budget-${budget}`}>
                  <span className='comparison-budget-label'>{formatRawBudget(budget)}</span>
                  <IconButton
                    size={Size.SM}
                    appearance={Appearance.EMPTY}
                    design={Design.NEUTRAL}
                    className='comparison-budget-remove-icon'
                    onClick={() => {
                      visibleComparisonBudgets.delete(budget);
                      setVisibleComparisonBudgets(current => new Set(current));
                    }}
                    disabled={graphView === 'showTable'}
                  >
                    <MdCancel />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            stretch={StretchMode.FIT}
            leadingIcon={<MdAdd />}
            size={Size.SM}
            className='add-comparison-budget-dropdown'
            value='comparison budget'
            menuItems={comparisonBudgetMenuItems}
            onToggle={isOpen => {
              setShowComparisonBudgetDropdown(isOpen);
              setStagedComparisonBudgets(new Set(visibleComparisonBudgets));
            }}
            onChange={val => {
              const valNum = Number(val);
              setStagedComparisonBudgets(current => {
                if (current.has(valNum)) {
                  current.delete(valNum);
                } else {
                  current.add(valNum);
                }
                return new Set(current);
              });
            }}
            disabled={graphView === 'showTable'}
            applyOnClick={() => {
              setVisibleComparisonBudgets(new Set(stagedComparisonBudgets));
            }}
            setShow={setShowComparisonBudgetDropdown}
            show={showComparisonBudgetDropdown}
            multiSelect
            hideCaret
          />
        </div>
      }
      rightActions={
        earliestVisibleDate && latestVisibleDate ? (
          <div className='forecast-results-right-actions'>
            {(userEarliestDate !== null || userLatestDate !== null) &&
              graphView === 'showGraph' && (
                <Button
                  size={Size.LG}
                  onClick={() => {
                    setUserEarliestDate(null);
                    setUserLatestDate(null);
                  }}
                >
                  Reset Date Range
                </Button>
              )}
            <DatePicker
              range={graphView === 'showGraph' ? graphDatePickerRange : tableDatePickerRange}
              onChange={({ start, end }) => {
                setEarliestVisibleDate(start as BpmDateString);
                setLatestVisibleDate(end as BpmDateString);
              }}
              isOutsideRange={date => {
                if (!minimumDate || !maximumDate) {
                  return true;
                }
                return date < minimumDate || date > maximumDate;
              }}
              disabled={graphView === 'showTable'}
              datePresets
              removeHeaderText
              maxDate={maximumDate}
              minDate={minimumDate}
            />
          </div>
        ) : undefined
      }
    >
      {flatForecastData && earliestVisibleDate && latestVisibleDate ? (
        <div className='forecast-results'>
          <ChartContainer
            rightActions={
              <div className='forecast-results-right-actions'>
                <IconSegmentedControlButton
                  options={[
                    { key: 'showTable', icon: <MdOutlineTableRows />, label: 'table view' },
                    { key: 'showGraph', icon: <MdShowChart />, label: 'graph view' }
                  ]}
                  selectedOption={graphView}
                  onChange={() =>
                    setGraphView(prev => (prev === 'showGraph' ? 'showTable' : 'showGraph'))
                  }
                  tooltipClass='forecast-results-graph-toggle-tooltip'
                />
                <DownloadDropdown
                  menuOptions={downloadOptions.map(option => option.label)}
                  onClickOptions={downloadOptions.map(option => option.onClick)}
                  disabledMenuOptions={downloadOptions.reduce((acc, option) => {
                    acc[option.label] = option.disabled ?? false;
                    return acc;
                  }, {})}
                />
              </div>
            }
            title={
              <>
                {showWIPFeatures && (
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    type={DropdownToggleType.WIDGET_TITLE}
                    value={chartDateGrouping}
                    options={DATE_GROUPING_OPTIONS}
                    onChange={option => {
                      setChartDateGrouping(option);
                      setError({
                        message: 'Updating date grouping of forecasting charts not yet supported'
                      });
                    }}
                  />
                )}
                <div>
                  {getCombinedChannelPlatform(forecast.channel, forecast.platform)}{' '}
                  {formatForecastViewLabel(forecast.outcomeVar, forecast.kpiName, selectedKpiView)}{' '}
                  and Spend
                </div>
                <div className='header-annotation'>(Historical and Forecasted)</div>
              </>
            }
          >
            {graphView === 'showGraph' ? (
              <ForecastResultsChart
                flatForecastData={flatForecastData}
                dataTimestampRanges={dataTimestampRanges}
                earliestVisibleDate={earliestVisibleDate}
                setEarliestVisibleDate={setEarliestVisibleDate}
                latestVisibleDate={latestVisibleDate}
                setLatestVisibleDate={setLatestVisibleDate}
                dateGrouping={chartDateGrouping}
                channel={forecast.channel}
                outcomeVar={forecast.outcomeVar}
                kpiName={forecast.kpiName}
                selectedKpiView={selectedKpiView}
                allComparisonBudgets={forecast.comparisonBudgets}
                selectedComparisonBudgets={allVisibleBudgets}
                syncId={2}
                primaryBudget={forecast.primaryBudget}
              />
            ) : (
              <ForecastResultsTable data={forecastResultsData} forecast={forecast} />
            )}
          </ChartContainer>
        </div>
      ) : (
        <div className='forecasting-widget-loader forecast-results-loader'>
          <Spinner size={50} />
        </div>
      )}
    </WidgetContainer>
  );
};
